import React, { useState } from "react";

import { Box, Popover, Typography } from "@mui/material";
import { Info } from "react-feather";

import { NO_DISPLAY_DATA } from "../../../../constants/index";
import {
  convertTimestampToDate,
  formatDateTimeDisplay,
} from "../../../../helpers/time";
import mainStyles from "../../../../styles";
import { OrgScimData } from "api/endpoints/scim";
import { SsoAdminData } from "api/endpoints/sso";

/**
 * SSO and SCIM state with the detail popover the full pane offers, so a card
 * keeps the same wording and the same click-through rather than flattening
 * either into read-only text.
 */
export interface SsoStatusProps {
  ssoData?: SsoAdminData;
}

export const SsoStatus = ({ ssoData }: SsoStatusProps) => {
  const classes = mainStyles();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (ssoData === undefined) return <>Loading...</>;

  const ssoEnabled = ssoData.authConf?.ssoConf?.ssoEnabled;
  const hasConnection = Boolean(
    ssoData.authConf?.ssoConf?.connDetails?.connectionName,
  );
  if (!ssoEnabled && !hasConnection) return <>Not configured</>;

  return (
    <>
      <Box
        component="span"
        sx={{ ...classes.clickable, cursor: "pointer" }}
        onClick={(event: React.MouseEvent<HTMLElement>) =>
          setAnchor(event.currentTarget)
        }
        data-testid={
          ssoEnabled ? "sso-enabled-display" : "sso-disabled-display"
        }
      >
        {ssoEnabled ? "ENABLED" : "DISABLED"}{" "}
        <Info size={14} style={{ verticalAlign: "middle" }} />
      </Box>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box m={2}>
          <Typography>
            {`Allow password login: ${(
              ssoData.authConf?.pwdConf?.pwdEnabled ?? false
            ).toString()}`}
          </Typography>
          <Typography>
            {`Domains: ${(ssoData.authConf?.ssoConf?.ssoDomains ?? [])
              .map((domainItem) => domainItem.domain)
              .join(", ")}`}
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export interface ScimStatusProps {
  scimData?: OrgScimData;
}

export const ScimStatus = ({ scimData }: ScimStatusProps) => {
  const classes = mainStyles();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (scimData === undefined) return <>Loading...</>;
  if (scimData.loadFailed) return <>Unavailable</>;
  if (!scimData.status?.hasScim) return <>No activity recorded</>;

  const status = scimData.status;

  return (
    <>
      <Box
        component="span"
        sx={{ ...classes.clickable, cursor: "pointer" }}
        onClick={(event: React.MouseEvent<HTMLElement>) =>
          setAnchor(event.currentTarget)
        }
        data-testid="scim-provisioned-display"
      >
        {"PROVISIONED "}
        <Info size={14} style={{ verticalAlign: "middle" }} />
      </Box>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box m={2}>
          <Typography>
            {`Users provisioned: ${status.userCount.toString()}`}
          </Typography>
          <Typography>
            {`Groups provisioned: ${status.groupCount.toString()}`}
          </Typography>
          <Typography>
            {`Last activity: ${
              status.lastModifiedAt
                ? formatDateTimeDisplay(
                    convertTimestampToDate(status.lastModifiedAt),
                  )
                : NO_DISPLAY_DATA
            }`}
          </Typography>
        </Box>
      </Popover>
    </>
  );
};
